import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import * as tasksActions from '../components/GridTasksNotes/actions/tasksActions';

type TasksContext = {
  tasksList: TaskRow[],
  taskDetails: Task,
  loadingTasksList: boolean,
  updateList: (shumaID: number) => void,
  getTaskDetails: (taskID: number) => void,
  resetTaskDetails: (taskID: number) => void,
  createVisitTask: (
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    notes: string,
    visitContactName: string,
    visitContactPhone: string
  ) => void,
  createGeneralTask: (
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    taskDescription: string,
  ) => void,
  editVisitTask: (
    taskID: number,
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    notes: string,
    visitContactName: string,
    visitContactPhone: string,
  ) => void,
  editGeneralTask: (
    taskID: number,
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    taskDescription: string,
  ) => void,
  deleteTask: (taskID: number) => void,
}

export type TaskRow = {
  taskID: number;
  createDate: Date;
  taskTypeID: number;
  taskType: string;
  taskStatusID: number;
  taskStatus: string;
  dueDate: Date;
  notes: string;
  createByUserID: number,
  createUserFirstName: string,
  createUserLastName: string,
  responsibleUserID: number,
  responsibleUserFirstName: string,
  responsibleUserLastName: string
}

export type Task = {
  taskID: number,
  shumaID: number,
  responsibleUserID: number,
  createDate: Date,
  taskTypeID: number,
  taskTypeName: string,
  taskStatusID: number,
  taskStatusName: string,
  dueDate: Date,
  notes: string,
  taskDescription: string,
  visitContactName: string,
  visitContactPhone: string,
}

interface TasksGridProviderProps {
  children: ReactNode;
  shumaID: number;
}

const emptyTask = {
  taskID: 0,
  shumaID: 0,
  responsibleUserID: 0,
  createDate: new Date(),
  taskTypeID: 0,
  taskTypeName: '',
  taskStatusID: 0,
  taskStatusName: '',
  dueDate: new Date(),
  notes: '',
  taskDescription: '',
  visitContactName: '',
  visitContactPhone: '',
};

const TasksGridContext = createContext<TasksContext>({
  tasksList: [],
  taskDetails: emptyTask,
  loadingTasksList: false,
  updateList: () => {},
  getTaskDetails: () => {},
  resetTaskDetails: () => {},
  createVisitTask: () => {},
  createGeneralTask: () => {},
  editVisitTask: () => {},
  editGeneralTask: () => {},
  deleteTask: () => {},
});

export const useTasksGridContext = () => useContext(TasksGridContext);

export const TasksGridProvider = ({children, shumaID}: TasksGridProviderProps) => {
  const [tasksList, setTasksList] = useState<TaskRow[]>([]);
  const [taskDetails, setTaskDetails] = useState<Task>(emptyTask);
  const [loadingTasksList, setLoadingTasksList] = useState(false);

  const updateList = async (shumaID: number) => {
    try {
      const tasksList = await tasksActions.getTasksList(shumaID);
      setTasksList([...tasksList]);
    } catch (e) {
      console.error(e);
      setTasksList([]);
    }
  };

  useEffect(() => {
    if (shumaID) {
      setLoadingTasksList(true);
      updateList(shumaID).finally(() => setLoadingTasksList(false));
    }
  }, [shumaID]);

  const getTaskDetails = async (taskID: number) => {
    if (taskID !== -1) {
      try {
        const data = await tasksActions.getTaskDetails(taskID);
        setTaskDetails(data);
      } catch (e) {
        console.error(e);
        setTaskDetails(emptyTask);
      }
    }
  };

  const resetTaskDetails = (taskID: number) => {
    if (taskID === -1) {
      setTaskDetails(emptyTask);
    }
  };

  const createVisitTask = async (
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    notes: string,
    visitContactName: string,
    visitContactPhone: string
  ) => {
    try {
      await tasksActions.addEditVisitTask({
        taskID: -1,
        shumaID,
        responsibleUserID,
        taskStatusID,
        dueDate,
        notes,
        visitContactName,
        visitContactPhone,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const createGeneralTask = async (
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    taskDescription: string,
  ) => {
    try {
      await tasksActions.addEditGeneralTask({
        taskID: -1,
        shumaID,
        responsibleUserID,
        taskStatusID,
        dueDate,
        taskDescription,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const editVisitTask = async (
    taskID: number,
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    notes: string,
    visitContactName: string,
    visitContactPhone: string,
  ) => {
    try {
      await tasksActions.addEditVisitTask({
        taskID,
        shumaID,
        responsibleUserID,
        taskStatusID,
        dueDate,
        notes,
        visitContactName,
        visitContactPhone,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const editGeneralTask = async (
    taskID: number,
    responsibleUserID: number,
    taskStatusID: number,
    dueDate: Date,
    taskDescription: string,
  ) => {
    try {
      await tasksActions.addEditGeneralTask({
        taskID,
        shumaID,
        responsibleUserID,
        taskStatusID,
        dueDate,
        taskDescription,
      });
    } catch (e) {
      console.error(e);
    }
  };


  const deleteTask = async (taskID: number) => {
    try {
      await tasksActions.deleteTask(taskID);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <TasksGridContext.Provider value={{
      tasksList,
      taskDetails,
      loadingTasksList,
      updateList,
      getTaskDetails,
      resetTaskDetails,
      createVisitTask,
      createGeneralTask,
      editVisitTask,
      editGeneralTask,
      deleteTask,
    }}>
      {children}
    </TasksGridContext.Provider>
  );
};